import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useMsal } from '@azure/msal-react';
import { notification } from 'antd';

import {
	BookedForSalesEvents,
	BookedForSalesProduct,
} from '../../../api-client';
import {
	AdminApiClient,
	BookedForSalesApiClient,
	CustomerApiClient,
} from '../../../providers/api-provider';
import {
	initCustomerBookedForSaleEventRecap,
	initCustomerBookedForSales,
} from '../../../slices/Customer/customerBookedProduct';

export const useBookedForSales = () => {
	const { instance } = useMsal();

	const dispatch = useDispatch();

	const [bookedForSalesProducts, setBookedForSalesProducts] =
		useState<BookedForSalesProduct[]>();
	const [bookedForSalesProductsLoading, setBookedForSalesProductsLoading] =
		useState<boolean>(false);

	const [
		customerBookedForSalesEventRecap,
		setCustomerBookedForSalesEventRecap,
	] = useState<BookedForSalesEvents>();
	const [
		customerBookedForSalesEventRecapLoading,
		setCustomerBookedForSalesEventRecapLoading,
	] = useState<boolean>(false);

	const [expiringBookedForSales, setexpiringBookedForSales] =
		useState<BookedForSalesProduct[]>();
	const [expiringBookedForSalesLoading, setexpiringBookedForSalesLoading] =
		useState<boolean>(false);

	const [
		exportBookedForSalesOrderLoading,
		setExportBookedForSalesOrderLoading,
	] = useState<boolean>(false);

	const handleBookForSales = async (
		customerId: number = -1,
		eventId: number,
		callback?: () => void,
	) => {
		setBookedForSalesProductsLoading(true);
		setCustomerBookedForSalesEventRecapLoading(true);

		const clientApi = await CustomerApiClient(instance);

		clientApi
			.getCustomerBookedForSalesByFilter(customerId, { eventId })
			.then((response) => {
				setBookedForSalesProducts(response.data);
				dispatch(initCustomerBookedForSales(response.data));
				callback && callback();
			})
			.catch((reason) => {
				setBookedForSalesProducts([]);

				notification.error({
					message: 'Unable to retrieve customer orders!',
					description: 'Unable to retrieve customer orders.',
					duration: 2,
				});
			})
			.finally(() => {
				setBookedForSalesProductsLoading(false);
			});

		clientApi
			.getCustomerBookedForSalesEventRecap(customerId, eventId)
			.then((response) => {
				setCustomerBookedForSalesEventRecap(response.data);
				dispatch(initCustomerBookedForSaleEventRecap([response.data]));
				callback && callback();
			})
			.catch((reason) => {
				setCustomerBookedForSalesEventRecap({});

				notification.error({
					message: 'Unable to retrieve customer orders!',
					description: 'Unable to retrieve customer orders.',
					duration: 2,
				});
			})
			.finally(() => {
				setCustomerBookedForSalesEventRecapLoading(false);
			});
	};

	const handleExpiringBookedForSales = async (
		salesAgentId: number,
		salesGroup: string | undefined,
	) => {
		if (!salesGroup) {
			const clientApi = await BookedForSalesApiClient(instance);

			clientApi
				.getExpiringBookedForSales(salesAgentId)
				.then((response) => {
					setexpiringBookedForSales(response.data);
				})
				.catch((reason) => {
					setexpiringBookedForSales([]);

					notification.error({
						message: 'Unable to retrieve expiring booked products!',
						description: 'Unable to retrieve expiring booked products.',
						duration: 2,
					});
				})
				.finally(() => {
					setexpiringBookedForSalesLoading(false);
				});
		} else {
			const adminApi = await AdminApiClient(instance);

			adminApi
				.getExpiringBookedForSalesBySalesGroup(salesGroup)
				.then((response) => {
					setexpiringBookedForSales(response.data);
				})
				.catch((reason) => {
					setexpiringBookedForSales([]);

					notification.error({
						message: 'Unable to retrieve expiring booked products!',
						description: 'Unable to retrieve expiring booked products.',
						duration: 2,
					});
				})
				.finally(() => {
					setexpiringBookedForSalesLoading(false);
				});
		}
	};

	const handleExportBookedForSalesOrder = async (
		customerName: string,
		id: number = -1,
	) => {
		setExportBookedForSalesOrderLoading(true);

		const clientApi = await BookedForSalesApiClient(instance);

		clientApi
			.printBookedForSalesOrder(id, { responseType: 'blob' })
			.then((response) => {
				saveAs(
					new Blob([response.data]),
					`${customerName.replace(/\s+/g, '')}-${new Date().toISOString().split('T')[0].replace(/-+/g, '')}-BFSORDER-PRODUCT_ID${id}.pdf`,
				);
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to export booked for sales order!',
					description: `Unable to export the booked for sales order.`,
					duration: 2,
				});
			})
			.finally(() => {
				setExportBookedForSalesOrderLoading(false);
			});
	};

	return {
		handleBookForSales,
		bookedForSalesProducts,
		bookedForSalesProductsLoading,
		customerBookedForSalesEventRecap,
		customerBookedForSalesEventRecapLoading,
		handleExpiringBookedForSales,
		expiringBookedForSales,
		expiringBookedForSalesLoading,
		handleExportBookedForSalesOrder,
		exportBookedForSalesOrderLoading,
	};
};
